import React, { useMemo, useState } from 'react';
import { SearchIcon } from '@fluentui/react-icons-mdl2';
import dayjs, { Dayjs } from 'dayjs';
import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ActionDialog from '@components/ActionDialog/ActionDialog';
import ActionButton from '@components/Button/ActionButton';
import If from '@components/If';
import TableLayout from '@components/TableLayout';
import useSnackbar from '@core/hooks/useSnackbar';
import { GetStudyResponse, StudyListItemModel } from '@core/types';
import { formatDate } from '@core/utils/formatDate';
import useMergeStudies from 'app/features/studies/hooks/useMergeStudies';
import useStudies from 'app/features/studies/hooks/useStudies';
import StudyRow from './StudyRow';
import useSx from './sx';

interface MergeStudiesModalProps {
  open: boolean;
  onClose: () => void;
  study?: GetStudyResponse;
}

export default function MergeStudiesModal({ open, onClose, study }: MergeStudiesModalProps) {
  const [selectedStudy, setSelectedStudy] = useState<StudyListItemModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().startOf('day'));
  const isDateValid = !selectedDate || selectedDate.isValid();

  const { studyList } = useStudies({
    filters: {
      search: searchTerm,
      dateRange:
        selectedDate && selectedDate.isValid()
          ? [selectedDate.toISOString(), selectedDate.endOf('day').toISOString()]
          : undefined,
      period: selectedDate && selectedDate.isValid() ? 3 : undefined,
      branchId: study?.branchId,
    },
    enabled: open && isDateValid,
  });

  const { isMerging, mergeStudiesAsync } = useMergeStudies();

  const showSnackbar = useSnackbar();
  const sx = useSx();

  const tableHeaders = useMemo(
    () => [
      { label: '', key: 'actions' },
      { label: 'MOD', key: 'modality' },
      { label: 'Paciente', key: 'patientFullName' },
      { label: 'Fecha', key: 'reportDate' },
    ],
    [],
  );

  const filteredStudyList = useMemo(() => {
    return studyList
      .filter((s) => s.studyInstanceUid !== study?.studyInstanceUid)
      .sort((a, b) => {
        // Mostrar primero coincidencias exactas con el apellido del paciente
        const aMatches = a.patientsFullName
          ?.toLowerCase()
          .includes(study?.patientsLastName?.toLowerCase() || '');
        const bMatches = b.patientsFullName
          ?.toLowerCase()
          .includes(study?.patientsLastName?.toLowerCase() || '');
        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0;
      });
  }, [studyList, study?.studyInstanceUid, study?.patientsLastName]);

  async function handleMergeStudies() {
    if (!study || !selectedStudy) return;
    const res = await mergeStudiesAsync({
      targetStudyInstanceUid: study.studyInstanceUid,
      sourceStudyInstanceUids: [selectedStudy.studyInstanceUid],
    });

    if (res.success) {
      setIsModalOpen(false);
      setSelectedDate(dayjs().startOf('day'));
      setSearchTerm('');
      setSelectedStudy(null);
      onClose();

      showSnackbar({
        message: 'Estudios unificados correctamente',
        title: 'Éxito',
        type: 'success',
      });
    } else {
      setIsModalOpen(false);
      showSnackbar({
        message: 'Error al unificar los estudios',
        title: 'Error',
        type: 'error',
      });
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      sx={sx.root}
      open={open}
      onClose={onClose}
      aria-labelledby="warning-dialog-title"
    >
      <DialogTitle id="warning-dialog-title" sx={sx.dialogHeader}>
        <Typography variant="h5" className="title">
          Unificar estudios
        </Typography>
        <IconButton color="inherit" onClick={onClose} aria-label="close" sx={sx.closeButton}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={sx.dialogContent}>
        <Typography>Seleccione los estudios a unificar</Typography>
        <Box sx={sx.filters}>
          <DatePicker
            localeText={{ start: 'Inicio' }}
            maxDate={dayjs().endOf('day')}
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            slotProps={{ textField: { size: 'small' } }}
            format="DD/MM/YYYY"
          />

          <Input
            placeholder="Buscar por paciente"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            sx={sx.searchInput}
          />
        </Box>
        <TableLayout headers={tableHeaders}>
          <If
            condition={filteredStudyList.length > 0}
            fallback={
              <TableRow>
                <TableCell colSpan={100} align="center">
                  No se encontraron estudios con los filtros aplicados
                </TableCell>
              </TableRow>
            }
          >
            {filteredStudyList.map((s) => (
              <StudyRow
                key={s.studyInstanceUid}
                study={s}
                onSelect={() => setSelectedStudy(s)}
                onDeselect={() => setSelectedStudy(null)}
                isSelected={selectedStudy?.studyInstanceUid === s.studyInstanceUid}
              />
            ))}
          </If>
        </TableLayout>
      </DialogContent>
      <DialogActions>
        <Box sx={sx.actionsButtonContainer}>
          <ActionButton color="secondary" variant="outlined" text={'Cancelar'} onClick={onClose} />
          <ActionButton
            isLoading={isMerging}
            disabled={!selectedStudy}
            color="primary"
            variant="contained"
            text={'Confirmar'}
            onClick={() => setIsModalOpen(true)}
          />
        </Box>
      </DialogActions>
      <ActionDialog
        open={isModalOpen}
        type="warning"
        onClose={() => {
          setIsModalOpen(false);
        }}
        onCancel={() => setIsModalOpen(false)}
        withPropagationStopped
        onConfirm={handleMergeStudies}
        title="Unificar estudios"
        message="¿Estás seguro de que deseas unificar estos estudios? Esta acción no se puede deshacer."
        isLoading={isMerging}
      >
        <Box>
          <Stack>
            <Typography variant="body2">
              {study?.patientsFullName || 'Paciente sin nombre'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {study?.referringPhysiciansName || 'Sin médico'}
              {' · '}
              {study?.studyDate ? formatDate(study.studyDate) : 'Sin fecha'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {study?.studyDescription || 'Sin descripción'}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack>
            <Typography variant="body2">
              {selectedStudy?.patientsFullName || 'Paciente sin nombre'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedStudy?.referringPhysiciansName || 'Sin médico'}
              {' · '}
              {selectedStudy?.studyDate ? formatDate(selectedStudy.studyDate) : 'Sin fecha'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedStudy?.studyDescription || 'Sin descripción'}
            </Typography>
          </Stack>
        </Box>
      </ActionDialog>
    </Dialog>
  );
}
